import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, CloudOff, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";

export function BridgeReadOnly({ resource, title, subtitle, columns, icon }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/bridge/${resource}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [resource]);

  const connected = data && data.connected;

  return (
    <div data-testid={`bridge-${resource}-page`}>
      <PageHeader title={title} subtitle={subtitle} />

      <div className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-2.5 ${connected ? "border-status-success-border bg-status-success-bg" : "border-fundio-border bg-white"}`}>
        {connected ? <CheckCircle2 className="h-4 w-4 text-status-success" /> : <CloudOff className="h-4 w-4 text-fundio-faint" />}
        <span className="text-xs text-fundio-muted">
          FUNDIO Bridge (read-only):{" "}
          <span className={`font-medium ${connected ? "text-status-success" : "text-status-warning"}`}>{connected ? "verbunden" : "nicht verbunden"}</span>
          {!connected && " – keine Live-Daten. Quelle: WordPress/Directorist (Master)."}
        </span>
      </div>

      <Panel testid={`bridge-${resource}-panel`} noPad>
        {data === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : !connected || (data.items || []).length === 0 ? (
          <div className="p-4">
            <EmptyState icon={icon} title="Bridge nicht verbunden – keine Live-Daten"
              description="Sobald die FUNDIO Bridge mit gültigen Zugangsdaten verbunden ist, werden hier die Live-Stammdaten aus Directorist read-only angezeigt. Es werden bewusst keine Demo-Daten dargestellt."
              testid={`bridge-${resource}-empty`} />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                {columns.map((c) => <th key={c.key} className="px-5 py-3">{c.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-fundio-border">
              {data.items.map((row, i) => (
                <tr key={row.external_id || i} className="bg-white" data-testid={`bridge-${resource}-row-${i}`}>
                  {columns.map((c) => <td key={c.key} className="px-5 py-3.5 text-fundio-ink">{row[c.key] ?? "—"}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
